'use client';

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import styles from './Toast.module.css';
import { IconCheck, IconArrowRight } from '@/components/Icons/Icons';
import { Link } from 'react-router-dom';

export type ToastItem = {
  id: string;
  title: string;
  description?: string;
  actionHref?: string;
  actionLabel?: string;
};

type ToastApi = {
  push: (item: Omit<ToastItem, 'id'>) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) window.clearTimeout(timer);
    timers.current.delete(id);
  }, []);

  const push = useCallback(
    (item: Omit<ToastItem, 'id'>) => {
      const id = `${Date.now()}_${Math.random().toString(16).slice(2)}`;
      const toast: ToastItem = { id, ...item };
      setToasts((prev) => [toast, ...prev].slice(0, 2));
      const timer = window.setTimeout(() => remove(id), 2600);
      timers.current.set(id, timer);
    },
    [remove]
  );

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.wrap} aria-live="polite" aria-relevant="additions removals">
        {toasts.map((t) => (
          <div key={t.id} className={styles.toast} role="status">
            <div className={styles.icon}>
              <IconCheck />
            </div>
            <div>
              <p className={styles.title}>{t.title}</p>
              {t.description ? <p className={styles.desc}>{t.description}</p> : null}
            </div>
            <div className={styles.actions}>
              {t.actionHref ? (
                <Link className="btn btnPrimary" to={t.actionHref} onClick={() => remove(t.id)}>
                  {t.actionLabel || 'Devam'} <IconArrowRight />
                </Link>
              ) : null}
              <button className="btn" onClick={() => remove(t.id)} aria-label="Kapat">
                Kapat
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
